import { FormikProps } from "formik";
import React, { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import { ITestable } from "@/browser";
import { VALIDATION_TYPES } from "@/components/Formik";
import { IConsultantProfileLanguage } from "@/data/consultant-profiles";
import { ILanguage } from "@/data/languages";
import { fetchSuggestedProfileData } from "@/data/profile-data";
import { Flex, FloatedList, Group, IChangeable, Section } from "@/eworkitComponents";
import { IWithFormikFieldProps, withFormikField } from "@/formikComponents";
import { useCodelistTranslation } from "@/hooks/Codelist";
import { useFetcher } from "@/hooks/http";
import { ICodelistGenericSelectParams } from "@/views/components/Codelists";
import { FormikCodelistGenericSelect } from "@/views/components/Formik/CodelistsGeneric";
import { SkillRemove } from "@/views/components/Forms/Skills/components";
import { SuggestedLanguagesList } from "@/views/components/Languages";
import { ILanguagesFormSectionValues } from "@/views/ConsultantProfiles/Detail/LanguagesSection/LanguagesForm";

import { GroupedLanguagesSelect } from "../../Codelists/GroupedLanguages";
import { SuggestedSection } from "../SuggestedData";

export const validationLanguagesSectionSchema = Yup.object().shape({
  languages: Yup.array().of(
    Yup.object().shape({
      level: Yup.string().required(VALIDATION_TYPES.REQUIRED),
    }),
  ),
});

interface ILanguagesSectionProps extends IChangeable, ITestable, IWithFormikFieldProps {
  value: IConsultantProfileLanguage[];
  label?: string;
  placeholder?: string;
  showSuggested?: boolean;
  groupWidth?: number;
  invalid?: boolean;
  setFieldTouched: FormikProps<ILanguagesFormSectionValues>["setFieldTouched"];
}

const LANGUAGE_LEVELS_CODELIST_QUERY: ICodelistGenericSelectParams = { codelistName: "language-levels" };

export const LanguagesSection: FC<ILanguagesSectionProps> = ({
  onChange,
  value,
  setFieldTouched,
  testName,
  label,
  invalid,
  showSuggested = true,
  groupWidth,
  name,
}) => {
  const { t } = useTranslation("session");
  const tCodelist = useCodelistTranslation();

  const suggestedDataFetcher = useFetcher(fetchSuggestedProfileData, {}, []);

  const handleAdd = () => (language: ILanguage | null) => {
    if (!language) {
      return;
    }

    if (onChange) {
      onChange([...value, { language }]);
    }
  };

  const handleRemove = () => (item: ILanguage) => {
    const languages = value.filter((v, i) => {
      const isRemoved = v.language === item;

      if (setFieldTouched && isRemoved) {
        setFieldTouched(`languages.${i}.level`, true);
      }

      return !isRemoved;
    });

    if (onChange) {
      onChange(languages);
    }
  };

  const handleSuggestedLanguageClick = useCallback(
    (language: ILanguage) => {
      handleAdd()(language);
    },
    [handleAdd],
  );

  const handleCheckedSuggestedLanguageClick = useCallback(
    (language: ILanguage) => {
      handleRemove()(language);
    },
    [handleRemove],
  );

  const renderBadge = (badgeValue: IConsultantProfileLanguage, i: number) => {
    return (
      <Flex align="center" key={`badge-${badgeValue.language}`}>
        <SkillRemove
          className="languages-section__badge"
          color="secondary"
          size="md"
          onClick={() => handleRemove()(badgeValue.language)}
          text={tCodelist("languages", badgeValue.language)}
          testName={`language[${badgeValue.language}]`}>
          <Flex grow={1}>
            <FormikCodelistGenericSelect
              size="sm"
              name={`${name}[${i}].level`}
              query={LANGUAGE_LEVELS_CODELIST_QUERY}
              value={badgeValue.level}
              isClearable={false}
              testName="language-level"
              simpleValue
              placeholder={t("CONSULTANT_PROFILES.DETAIL.LANGUAGES.SENIORITY_PLACEHOLDER")}
            />
          </Flex>
        </SkillRemove>
      </Flex>
    );
  };

  return (
    <Section direction="column" innerMargin="md" testName={testName}>
      <Flex>
        <Group
          label={label || t("CONSULTANT_PROFILES.DETAIL.LANGUAGES.SEARCH")}
          direction="column"
          width={groupWidth || { xs: 12, md: 5 }}
          grow={1}>
          <GroupedLanguagesSelect
            onChange={handleAdd()}
            testName="languages"
            placeholder={t("CONSULTANT_PROFILES.DETAIL.LANGUAGES.PLACEHOLDER")}
            invalid={invalid}
            filterLanguages={value.map(v => v.language)}
          />
        </Group>
      </Flex>

      {!!value.length && <FloatedList size="sm">{value.map(renderBadge)}</FloatedList>}

      {showSuggested && suggestedDataFetcher.payload && (
        <SuggestedSection header={t("CONSULTANT_PROFILES.DETAIL.SUGGESTED_DATA.PROFILE_FORM.SUGGESTED_LANGUAGES")}>
          <SuggestedLanguagesList
            suggestedLanguages={suggestedDataFetcher.payload.languages}
            onClick={handleSuggestedLanguageClick}
            onCheckedClick={handleCheckedSuggestedLanguageClick}
            selectedLanguages={value?.map(l => l.language)}
          />
        </SuggestedSection>
      )}
    </Section>
  );
};

export const FormikLanguagesSection = withFormikField(LanguagesSection);
